import { SubmitHandler } from 'react-hook-form'
import zod from 'zod'
import { Form, ButtonLoader, cn } from '@wellesley/ui'
import { FC, useState } from 'react'
import { usePostApiV1AccountsAuthenticateEmailMutation } from '../../../authApi'
import { AppsLink, useAppsSearchParams } from '@wellesley/router'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import CommonFormField from 'components/form/CommonFormField'
import {
  useCommonForm,
  usernameOrEmailValidator,
  maxPwdLength,
} from '@wellesley/forms'
import { logger } from 'helpers/logger'
import isInvalidCredsError from '../../../helpers/isInvalidCredsError'

const formSchema = zod.object({
  login: usernameOrEmailValidator,
  password: zod.string().trim().min(1, ''),
})

const invalidCredsError = 'Invalid credentials. Please try again'

export type RegisterInput = zod.TypeOf<typeof formSchema>

export const fixedCacheKey = 'usePostApiV1AccountsAuthenticateEmailMutation'
export type LoginFormProps = {
  showSignupLink?: boolean
  loginButtonName?: string
  isLoading?: boolean
  signupRedirectUrl?: string
  description?: string
}
export const LoginForm: FC<LoginFormProps> = ({
  isLoading = false,
  showSignupLink = true,
  loginButtonName = 'Sign in',
  signupRedirectUrl,
  description = 'Welcome back, friend. Sign in to continue',
}) => {
  const form = useCommonForm({
    schema: formSchema,
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      login: '',
      password: '',
    },
    showRequiredErrors: false,
  })

  const { control, handleSubmit } = form
  const [searchParams] = useAppsSearchParams()
  const redirectUrl = searchParams.get('redirectUrl')

  const [registerUser, { isLoading: isAuthLoading }] =
    usePostApiV1AccountsAuthenticateEmailMutation({ fixedCacheKey })

  const [isCredsError, setIsCredsError] = useState(false)

  const onSubmit: SubmitHandler<RegisterInput> = async (values) => {
    try {
      await registerUser({ authRequest: values }).unwrap()
    } catch (e) {
      if (isInvalidCredsError(e as FetchBaseQueryError)) {
        form.setError('login', { message: '' })
        form.setError('password', { message: '' })
        setIsCredsError(true)
      } else {
        logger.error(`Login error: ${JSON.stringify(e)}`)
      }
    }
  }

  const resetCredsError = () => {
    isCredsError && setIsCredsError(false)
  }

  const handleLoginChange = () => {
    resetCredsError()
    form.clearErrors('login')
  }

  const handlePasswordChange = () => {
    resetCredsError()
    form.clearErrors('password')
  }

  const isDirty =
    Object.keys(form.formState.dirtyFields).length ===
    Object.keys(form.getValues()).length

  return (
    <Form {...form}>
      <form
        className="p-m"
        onSubmit={(...args) => void handleSubmit(onSubmit)(...args)}
      >
        <div className="m-auto w-full md:w-[450px]">
          <div className="m-auto flex w-full flex-col gap-s">
            <div
              className={cn(
                'p-xxs text-center text-base font-medium text-gray-500',
                {
                  'text-red-500': isCredsError,
                },
              )}
            >
              {isCredsError ? invalidCredsError : description}
            </div>
            <CommonFormField
              control={control}
              name="login"
              onFieldChange={handleLoginChange}
              displayName="Username or Email"
            />
            <CommonFormField
              type="password"
              control={control}
              name="password"
              onFieldChange={handlePasswordChange}
              displayName="Password"
              maxChars={maxPwdLength}
              className="mb-xs"
            />
          </div>
          <div className="flex cursor-pointer pb-s text-base font-semibold text-blue-500">
            <AppsLink to="/forgot-password">I forgot my password</AppsLink>
          </div>
        </div>

        <div className="flex flex-col items-end pt-m">
          <ButtonLoader
            disabled={!isDirty}
            data-testid="login-button"
            type="submit"
            size="m"
            isLoading={isAuthLoading || isLoading}
          >
            {loginButtonName}
          </ButtonLoader>
          {showSignupLink && (
            <div className="m-auto mt-s text-sm font-normal text-gray-700">
              Don’t have an account?{' '}
              <AppsLink
                className="font-semibold text-blue-500"
                to="/signup"
                props={{ redirectUrl: signupRedirectUrl || redirectUrl || '' }}
              >
                Create account
              </AppsLink>
            </div>
          )}
        </div>
      </form>
    </Form>
  )
}
